import React, { useState } from 'react';
import {
  PlayCircle,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info
} from 'lucide-react';
import { EnhancedTemplate } from '../../types/templateTypes';
import { findCanonicalPaymentType } from '../../utils/paymentTypeMasterRegistry';

interface TemplateTesterProps {
  template: EnhancedTemplate;
  sampleData: any[];
}

interface TestResult {
  success: boolean;
  recordsProcessed: number;
  recordsFailed: number;
  errors: string[];
  warnings: string[];
  summary: {
    upfrontGross: number;
    upfrontAdjustments: number;
    trailGross: number;
    trailAdjustments: number;
    totalBrokers: number;
    totalLoans: number;
    unmappedPaymentTypes: string[];
  };
  processedRecords: any[];
  failedRecords: any[];
}

const TemplateTester: React.FC<TemplateTesterProps> = ({
  template,
  sampleData
}) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'errors' | 'warnings' | 'processed'>('summary');

  const runTest = async () => {
    setTesting(true);

    try {
      const result: TestResult = {
        success: false,
        recordsProcessed: 0,
        recordsFailed: 0,
        errors: [],
        warnings: [],
        summary: {
          upfrontGross: 0,
          upfrontAdjustments: 0,
          trailGross: 0,
          trailAdjustments: 0,
          totalBrokers: 0,
          totalLoans: 0,
          unmappedPaymentTypes: []
        },
        processedRecords: [],
        failedRecords: []
      };

      const brokers = new Set<string>();
      const loans = new Set<string>();
      const unmappedTypes = new Set<string>();

      // Process each record
      for (const rawRecord of sampleData) {
        try {
          const processedRecord: any = {};

          // Apply column mappings
          for (const [dbField, mapping] of Object.entries(template.columnMappings)) {
            const value = rawRecord[mapping.sourceColumn];

            if (value !== undefined && value !== '') {
              // Apply transformations
              if (mapping.transform === 'currency') {
                // Convert to cents
                const numValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
                processedRecord[dbField] = Math.round(numValue * 100);
              } else if (mapping.transform === 'date') {
                // Parse date (simplified for demo)
                processedRecord[dbField] = new Date(value).toISOString();
              } else if (mapping.transform === 'percentage') {
                processedRecord[dbField] = parseFloat(value);
              } else {
                processedRecord[dbField] = value;
              }
            }
          }

          // Handle payment type
          const paymentTypeValue = rawRecord[template.paymentTypeConfig.columnName];
          const mappedPaymentType = template.paymentTypeConfig.customMappings?.[paymentTypeValue] ||
            findCanonicalPaymentType(paymentTypeValue)?.canonicalName;

          if (!mappedPaymentType) {
            unmappedTypes.add(paymentTypeValue);
            result.warnings.push(`Unmapped payment type: ${paymentTypeValue}`);
          }

          processedRecord.paymentType = mappedPaymentType || 'unknown';

          // Transform deductions to negative
          const amount = processedRecord.amountExGst || 0;
          if (mappedPaymentType?.includes('deduction') && amount > 0) {
            processedRecord.reportingAmount = -amount;
          } else {
            processedRecord.reportingAmount = amount;
          }

          // Aggregate for summary
          if (mappedPaymentType?.includes('upfront')) {
            if (processedRecord.reportingAmount >= 0) {
              result.summary.upfrontGross += processedRecord.reportingAmount;
            } else {
              result.summary.upfrontAdjustments += processedRecord.reportingAmount;
            }
          } else if (mappedPaymentType?.includes('trail')) {
            if (processedRecord.reportingAmount >= 0) {
              result.summary.trailGross += processedRecord.reportingAmount;
            } else {
              result.summary.trailAdjustments += processedRecord.reportingAmount;
            }
          } else if (mappedPaymentType === 'adjustment') {
            // Generic adjustments need context-based categorization
            // Check LINE_OF_BUSINESS or similar fields to determine upfront vs trail
            const lineOfBusiness = rawRecord['LINE_OF_BUSINESS'] || rawRecord['Product Type'] || '';
            const isGeneralInsurance = lineOfBusiness.toLowerCase().includes('general insurance');

            // General Insurance adjustments are typically upfront-related
            // Residential Mortgage adjustments could be either - default to upfront for now
            // Negative adjustments are clawbacks/corrections
            if (processedRecord.reportingAmount < 0) {
              if (isGeneralInsurance) {
                result.summary.upfrontAdjustments += processedRecord.reportingAmount;
              } else {
                // Default unknown adjustments to upfront
                result.summary.upfrontAdjustments += processedRecord.reportingAmount;
              }
            } else {
              // Positive adjustments (rare, but handle them)
              if (isGeneralInsurance) {
                result.summary.upfrontGross += processedRecord.reportingAmount;
              } else {
                result.summary.upfrontGross += processedRecord.reportingAmount;
              }
            }
          }

          // Track unique entities
          if (processedRecord.broker) brokers.add(processedRecord.broker);
          if (processedRecord.loanId) loans.add(processedRecord.loanId);

          result.processedRecords.push(processedRecord);
          result.recordsProcessed++;
        } catch (error) {
          result.recordsFailed++;
          result.failedRecords.push({ ...rawRecord, error: String(error) });
          result.errors.push(`Row ${result.recordsProcessed + result.recordsFailed}: ${error}`);
        }
      }

      // Set summary
      result.summary.totalBrokers = brokers.size;
      result.summary.totalLoans = loans.size;
      result.summary.unmappedPaymentTypes = Array.from(unmappedTypes);
      result.success = result.recordsFailed === 0 && result.errors.length === 0;

      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        recordsProcessed: 0,
        recordsFailed: sampleData.length,
        errors: [String(error)],
        warnings: [],
        summary: {
          upfrontGross: 0,
          upfrontAdjustments: 0,
          trailGross: 0,
          trailAdjustments: 0,
          totalBrokers: 0,
          totalLoans: 0,
          unmappedPaymentTypes: []
        },
        processedRecords: [],
        failedRecords: sampleData
      });
    } finally {
      setTesting(false);
    }
  };

  const successRate = testResult
    ? (testResult.recordsProcessed / (testResult.recordsProcessed + testResult.recordsFailed)) * 100
    : 0;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(Math.abs(cents) / 100);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <button
          onClick={runTest}
          disabled={!template.name || !template.aggregator || sampleData.length === 0 || testing}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg transition-colors font-medium"
        >
          <PlayCircle className={`w-5 h-5 ${testing ? 'animate-pulse' : ''}`} />
          {testing ? 'Running Test...' : 'Run Test with Sample Data'}
        </button>

        {sampleData.length === 0 && (
          <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 inline-block">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <h4 className="text-yellow-400 font-medium">No sample data available</h4>
                <p className="text-slate-400 text-sm mt-1">
                  Upload a sample file in Step 1 to test the template
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {testResult && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Success Rate</span>
                <div className={`w-2 h-2 rounded-full ${
                  successRate === 100 ? 'bg-green-500' : successRate > 90 ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
              </div>
              <div className="text-2xl font-bold text-white">{successRate.toFixed(1)}%</div>
              <div className="w-full bg-slate-700 rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full ${
                    successRate === 100 ? 'bg-green-500' : successRate > 90 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${successRate}%` }}
                />
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Records Processed</span>
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <div className="text-2xl font-bold text-white">{testResult.recordsProcessed}</div>
            </div>

            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Records Failed</span>
                <XCircle className="w-4 h-4 text-red-400" />
              </div>
              <div className={`text-2xl font-bold ${testResult.recordsFailed > 0 ? 'text-red-400' : 'text-white'}`}>
                {testResult.recordsFailed}
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Warnings</span>
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
              </div>
              <div className={`text-2xl font-bold ${testResult.warnings.length > 0 ? 'text-yellow-400' : 'text-white'}`}>
                {testResult.warnings.length}
              </div>
            </div>
          </div>

          {/* Commission Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
              <h4 className="text-slate-300 font-medium mb-3">Upfront Commissions</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Gross</span>
                  <span className="text-green-400 font-medium">
                    {formatCurrency(testResult.summary.upfrontGross)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Adjustments</span>
                  <span className="text-red-400 font-medium">
                    -{formatCurrency(testResult.summary.upfrontAdjustments)}
                  </span>
                </div>
                <div className="border-t border-slate-700 pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300 text-sm font-medium">Net</span>
                    <span className="text-white font-bold">
                      {formatCurrency(testResult.summary.upfrontGross + testResult.summary.upfrontAdjustments)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
              <h4 className="text-slate-300 font-medium mb-3">Trail Commissions</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Gross</span>
                  <span className="text-green-400 font-medium">
                    {formatCurrency(testResult.summary.trailGross)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Adjustments</span>
                  <span className="text-red-400 font-medium">
                    -{formatCurrency(testResult.summary.trailAdjustments)}
                  </span>
                </div>
                <div className="border-t border-slate-700 pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300 text-sm font-medium">Net</span>
                    <span className="text-white font-bold">
                      {formatCurrency(testResult.summary.trailGross + testResult.summary.trailAdjustments)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Entity Counts */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4 text-center">
              <div className="text-slate-400 text-sm mb-1">Unique Brokers</div>
              <div className="text-2xl font-bold text-white">{testResult.summary.totalBrokers}</div>
            </div>
            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4 text-center">
              <div className="text-slate-400 text-sm mb-1">Unique Loans</div>
              <div className="text-2xl font-bold text-white">{testResult.summary.totalLoans}</div>
            </div>
            <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4 text-center">
              <div className="text-slate-400 text-sm mb-1">Unmapped Types</div>
              <div className={`text-2xl font-bold ${
                testResult.summary.unmappedPaymentTypes.length > 0 ? 'text-yellow-400' : 'text-green-400'
              }`}>
                {testResult.summary.unmappedPaymentTypes.length}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-slate-900/50 rounded-lg border border-slate-700/50">
            <div className="flex border-b border-slate-700">
              {[
                { id: 'summary', label: 'Summary' },
                { id: 'errors', label: `Errors (${testResult.errors.length})`, count: testResult.errors.length },
                { id: 'warnings', label: `Warnings (${testResult.warnings.length})`, count: testResult.warnings.length },
                { id: 'processed', label: 'Processed Data Sample' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-white bg-slate-800/50 border-b-2 border-blue-500'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'summary' && (
                <div className="space-y-4">
                  {testResult.success ? (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-green-400 font-medium">Template Test Passed</h4>
                          <p className="text-slate-400 text-sm mt-1">
                            All records were processed successfully. The template is ready to use.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-red-400 font-medium">Template Test Has Issues</h4>
                          <p className="text-slate-400 text-sm mt-1">
                            {testResult.errors.length} errors and {testResult.warnings.length} warnings found. Review and fix before using the template.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {testResult.summary.unmappedPaymentTypes.length > 0 && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-yellow-400 font-medium">Unmapped Payment Types</h4>
                          <p className="text-slate-400 text-sm mt-1 mb-2">
                            The following payment types could not be mapped:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {testResult.summary.unmappedPaymentTypes.map(type => (
                              <span key={type} className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">
                                {type}
                              </span>
                            ))}
                          </div>
                          <p className="text-slate-400 text-sm mt-2">
                            Go back to Step 4 to map these types.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'errors' && (
                <div>
                  {testResult.errors.length > 0 ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h4 className="text-red-400 font-medium mb-2">Processing Errors</h4>
                      <ul className="text-slate-400 text-sm space-y-1">
                        {testResult.errors.slice(0, 10).map((error, i) => (
                          <li key={i}>• {error}</li>
                        ))}
                        {testResult.errors.length > 10 && (
                          <li className="text-slate-500">... and {testResult.errors.length - 10} more</li>
                        )}
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-green-400 font-medium">No Errors</h4>
                          <p className="text-slate-400 text-sm mt-1">
                            All records processed without errors
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'warnings' && (
                <div>
                  {testResult.warnings.length > 0 ? (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <h4 className="text-yellow-400 font-medium mb-2">Processing Warnings</h4>
                      <ul className="text-slate-400 text-sm space-y-1">
                        {testResult.warnings.slice(0, 10).map((warning, i) => (
                          <li key={i}>• {warning}</li>
                        ))}
                        {testResult.warnings.length > 10 && (
                          <li className="text-slate-500">... and {testResult.warnings.length - 10} more</li>
                        )}
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-green-400 font-medium">No Warnings</h4>
                          <p className="text-slate-400 text-sm mt-1">
                            All records processed without warnings
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'processed' && testResult.processedRecords.length > 0 && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800/50">
                        <tr>
                          {Object.keys(testResult.processedRecords[0] || {}).slice(0, 5).map(key => (
                            <th key={key} className="px-4 py-2 text-left text-slate-400 font-medium text-sm">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {testResult.processedRecords.slice(0, 5).map((record, idx) => (
                          <tr key={idx}>
                            {Object.entries(record).slice(0, 5).map(([key, value]) => (
                              <td key={key} className="px-4 py-2 text-slate-300 text-sm">
                                {key.includes('Amount') || key === 'reportingAmount'
                                  ? formatCurrency(value as number)
                                  : String(value)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-slate-500 text-sm">
                    Showing first 5 of {testResult.processedRecords.length} processed records
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateTester;